import React, { useState } from "react";
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  Box,
  Tooltip,
  Snackbar,
  Skeleton,
} from "@mui/material";
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import {
  useGetApiKeysQuery,
  useCreateApiKeyMutation,
  useRevokeApiKeyMutation,
  useGetOrganizationsQuery,
} from "../../redux/slices/djangoApi";

const ApiKeys = () => {
  const { data: apiKeys, isLoading } = useGetApiKeysQuery();
  const { data: orgsData } = useGetOrganizationsQuery();
  const [createApiKey] = useCreateApiKeyMutation();
  const [revokeApiKey] = useRevokeApiKeyMutation();

  const [createOpen, setCreateOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [createdKey, setCreatedKey] = useState(null);
  const [snackbar, setSnackbar] = useState(null);

  const [form, setForm] = useState({
    name: "",
    organization: "",
    can_view_status: true,
    can_reboot: true,
    can_power_on: true,
    can_power_off: false,
  });

  const organizations = orgsData?.results || orgsData || [];

  const handleCreate = async () => {
    try {
      const result = await createApiKey({
        ...form,
        organization: Number(form.organization),
      }).unwrap();
      setCreatedKey(result.raw_key);
      setCreateOpen(false);
      setForm({
        name: "",
        organization: "",
        can_view_status: true,
        can_reboot: true,
        can_power_on: true,
        can_power_off: false,
      });
    } catch (err) {
      setSnackbar({
        severity: "error",
        message: err?.data?.detail || "Failed to create API key",
      });
    }
  };

  const handleRevoke = async () => {
    try {
      await revokeApiKey(revokeTarget.id).unwrap();
      setRevokeTarget(null);
      setSnackbar({ severity: "success", message: "API key revoked." });
    } catch {
      setSnackbar({ severity: "error", message: "Failed to revoke API key." });
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ severity: "success", message: "Copied to clipboard." });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const keys = apiKeys?.results || apiKeys || [];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" fontWeight="bold">
          API Keys
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          Create API Key
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Key Prefix</TableCell>
              <TableCell>Organization</TableCell>
              <TableCell>Permissions</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Last Used</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 8 }).map((_, j) => (
                      <TableCell key={j}>
                        <Skeleton />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : keys.map((key) => (
                  <TableRow
                    key={key.id}
                    sx={{ opacity: key.is_active ? 1 : 0.5 }}
                  >
                    <TableCell>{key.name}</TableCell>
                    <TableCell>
                      <code>{key.key_prefix}...</code>
                    </TableCell>
                    <TableCell>{key.organization_name || "-"}</TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {key.can_view_status && (
                          <Chip label="Status" size="small" />
                        )}
                        {key.can_reboot && <Chip label="Reboot" size="small" />}
                        {key.can_power_on && (
                          <Chip label="Power On" size="small" />
                        )}
                        {key.can_power_off && (
                          <Chip label="Power Off" size="small" color="error" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(key.created_at)}</TableCell>
                    <TableCell>{formatDate(key.last_used_at)}</TableCell>
                    <TableCell>
                      <Chip
                        label={key.is_active ? "Active" : "Revoked"}
                        color={key.is_active ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      {key.is_active && (
                        <Tooltip title="Revoke">
                          <IconButton
                            color="error"
                            onClick={() => setRevokeTarget(key)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            {!isLoading && keys.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    No API keys yet. Create one to get started.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Dialog */}
      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create API Key</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder='e.g., "FastRTK Production"'
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Organization</InputLabel>
            <Select
              value={form.organization}
              label="Organization"
              onChange={(e) =>
                setForm({ ...form, organization: e.target.value })
              }
            >
              {organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Permissions
          </Typography>
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.can_view_status}
                  onChange={(e) =>
                    setForm({ ...form, can_view_status: e.target.checked })
                  }
                />
              }
              label="View device status"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.can_reboot}
                  onChange={(e) =>
                    setForm({ ...form, can_reboot: e.target.checked })
                  }
                />
              }
              label="Reboot devices"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.can_power_on}
                  onChange={(e) =>
                    setForm({ ...form, can_power_on: e.target.checked })
                  }
                />
              }
              label="Power on devices"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={form.can_power_off}
                  onChange={(e) =>
                    setForm({ ...form, can_power_off: e.target.checked })
                  }
                />
              }
              label="Power off devices (dangerous)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!form.name || !form.organization}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Created Key Dialog */}
      <Dialog
        open={!!createdKey}
        onClose={() => setCreatedKey(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>API Key Created</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Save this key now. It will not be shown again.
          </Alert>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 2,
              bgcolor: "grey.100",
              borderRadius: 1,
              fontFamily: "monospace",
              fontSize: "1rem",
              wordBreak: "break-all",
            }}
          >
            {createdKey}
            <IconButton onClick={() => handleCopy(createdKey)} size="small">
              <CopyIcon />
            </IconButton>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setCreatedKey(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke Confirmation */}
      <Dialog open={!!revokeTarget} onClose={() => setRevokeTarget(null)}>
        <DialogTitle>Revoke API Key</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to revoke{" "}
            <strong>{revokeTarget?.name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRevoke}>
            Revoke
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {snackbar && (
          <Alert
            onClose={() => setSnackbar(null)}
            severity={snackbar.severity}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        )}
      </Snackbar>
    </Container>
  );
};

export default ApiKeys;
